import { supabase } from "@/integrations/supabase/client";
import { documentWriteError } from "./pinnedDocuments";
import { bulkMoveToTrash } from "@/stores/documents/repository/mutations";
import type { DocumentFolder, TreeFolder } from "../types";
import { extractFolderName, extractCategory } from "../types";
import { ALL_PREDEFINED_FOLDER_NAMES } from "../constants/folders";
import { sortTreeFolders } from "../utils/naturalSort";
import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "../constants/systemFolders";

/**
 * WHA-3270 — the system-managed folder that holds files uploaded via an
 * issue's attachment flow. Surface read-only in /projects and
 * /buildings/:id/documents — no rename / delete / upload-into / create-
 * subfolder allowed. The issue attachment flows create it, so creation is
 * system-driven.
 */
export { ISSUE_ATTACHMENTS_FOLDER_NAME };

/**
 * Returns true if the folder is system-managed and must not be mutated
 * directly by the user. Currently a single name match; if more system
 * folders appear we'll likely promote this to an `is_system` column on
 * `document_folders` rather than expanding the name list.
 */
export function isSystemFolder(folder: { name?: string | null } | null | undefined): boolean {
  if (!folder) return false;
  return folder.name === ISSUE_ATTACHMENTS_FOLDER_NAME;
}

/**
 * Same check but accepting a name only (for code paths that don't have the
 * full folder row, e.g. folder-creation validation).
 */
export function isSystemFolderName(name: string | null | undefined): boolean {
  return name === ISSUE_ATTACHMENTS_FOLDER_NAME;
}

/**
 * What makes a `document_folders` row a folder in a project's tree: custom,
 * project-scoped and not trashed. A project also holds rows carrying neither
 * flag — archive containers, system folders, rows made by a backend or an
 * older build — and none of those are drawn as, or countable as, subfolders
 * of the project.
 *
 * NOT the set `getProjectFolders` returns, and deliberately so since the
 * develop merge: that query dropped the two flags because they default to
 * false and nothing enforces them (the SharePoint sync writes `is_custom` and
 * omits `is_project_scoped`), so filtering on them made real folders vanish
 * from the tree. Read its docstring before reusing this helper anywhere that
 * has to agree with what the tree draws.
 *
 * The organise modal's counts (WHA-3654) are moving to the
 * `preview_organize_scope` RPC, which counts through
 * `private.organize_scope_documents` — the same predicate the request guard
 * enforces — so they no longer depend on this helper agreeing with anything.
 */
export function applyProjectFolderFilters<T extends { eq(column: string, value: boolean): T }>(
  query: T
): T {
  return query.eq("is_custom", true).eq("is_project_scoped", true).eq("is_deleted", false);
}

/**
 * Recursively collect all descendant folder IDs from a TreeFolder node.
 * Does NOT include the folder itself — only its children, grandchildren, etc.
 */
export function getDescendantFolderIds(folder: TreeFolder): string[] {
  const ids: string[] = [];
  for (const child of folder.children) {
    ids.push(child.id);
    ids.push(...getDescendantFolderIds(child));
  }
  return ids;
}

export const folderService = {
  async getFolders(
    buildingId: string,
    projectId?: string,
    templateType?: string
  ): Promise<DocumentFolder[]> {
    let query = supabase
      .from("document_folders")
      .select("*")
      .eq("building_id", parseInt(buildingId, 10))
      .eq("is_deleted", false);

    if (projectId) {
      query = query.eq("project_id", parseInt(projectId, 10));
    }

    if (templateType) {
      query = query.eq("template_type", templateType);
    }

    const { data, error } = await query
      .order("folder_depth", { ascending: true })
      .order("folder_path");

    if (error) throw error;
    return data || [];
  },

  /**
   * Every live folder of a project, for the Folders tab's tree.
   *
   * Scoped by building + project + not-deleted, and deliberately NOT by
   * `is_custom` / `is_project_scoped`. Those two flags are set by
   * `get_or_create_folder`, `createCustomFolder` and `copy_folder`, but they
   * default to false and nothing enforces them — the SharePoint sync, for one,
   * writes `is_custom` and omits `is_project_scoped` entirely. A folder missing
   * either flag stayed navigable everywhere else (the List tab's own folder map
   * filters neither) while vanishing from this tree, and a document inside it
   * then had nowhere to appear: not at the root (`folder_id` is set) and not in
   * a folder (its folder is not a node). This query now matches the folder set
   * the rest of the app navigates.
   */
  async getProjectFolders(buildingId: string, projectId: string): Promise<DocumentFolder[]> {
    // Paged past PostgREST's 1000-row default cap, which truncates silently.
    // A truncated tree IS the bug above — folders the rest of the app navigates
    // missing from this list, and the documents inside them with nowhere to
    // render. `id` completes the sort so offset paging cannot repeat or skip a
    // row when two folders share a depth and a path.
    //
    // Deliberately NOT applyProjectFolderFilters (WHA-3654 merge): that helper
    // also requires is_custom AND is_project_scoped, which is precisely the
    // pair this function's docstring above explains it must not filter on.
    const PAGE_SIZE = 1000;
    const folders: DocumentFolder[] = [];

    for (let offset = 0; ; offset += PAGE_SIZE) {
      const { data, error } = await supabase
        .from("document_folders")
        .select("*")
        .eq("building_id", parseInt(buildingId, 10))
        .eq("project_id", parseInt(projectId, 10))
        .eq("is_deleted", false)
        .order("folder_depth", { ascending: true })
        .order("folder_path")
        .order("id")
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) throw error;
      const page = data ?? [];
      folders.push(...page);
      if (page.length < PAGE_SIZE) return folders;
    }
  },

  // Get folder by ID with path information
  async getFolderById(folderId: string): Promise<DocumentFolder | null> {
    const { data, error } = await supabase
      .from("document_folders")
      .select("*")
      .eq("id", folderId)
      .eq("is_deleted", false)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Get full folder hierarchy path without relying on DB function (no name column in DB)
  async getFolderPath(folderId: string): Promise<DocumentFolder[]> {
    const path: DocumentFolder[] = [];
    let currentId: string | null = folderId;
    const safety = new Set<string>();

    while (currentId) {
      if (safety.has(currentId)) break; // prevent accidental loops
      safety.add(currentId);

      const { data: folder, error } = await supabase
        .from("document_folders")
        .select(
          "id, parent_id, folder_path, folder_depth, is_custom, building_id, project_id, created_by, created_at, modified_by, modified_at, org_id, folder_config"
        )
        .eq("id", currentId)
        .eq("is_deleted", false)
        .maybeSingle();

      if (error) throw error;
      if (!folder) break;

      path.unshift(folder as unknown as DocumentFolder);
      currentId = folder.parent_id ? String(folder.parent_id) : null;
    }

    return path;
  },

  // Single canonical folder-trash path: defer to bulkMoveToTrash so the
  // multiselect bar, the folder context menu, and the customFolders mutation
  // all produce the same DB writes (descendant docs + folder rows soft-
  // deleted in one transaction-style sequence).
  async deleteFolder(folderId: string, userId: string): Promise<void> {
    await bulkMoveToTrash([], userId, [folderId]);
  },

  async getAvailablePredefinedFolders(
    buildingId: string,
    category: "documents" | "drawings" | "unsorted"
  ): Promise<(typeof ALL_PREDEFINED_FOLDER_NAMES)[number][]> {
    // Get existing folders for this building and category
    const { data: existingFolders, error } = await supabase
      .from("document_folders")
      .select("folder_path, folder_config")
      .eq("building_id", parseInt(buildingId, 10))
      .eq("is_deleted", false);

    if (error) throw error;

    const existingNames = (existingFolders || [])
      .filter(
        (f) =>
          extractCategory(f.folder_config) === (category === "unsorted" ? "documents" : category)
      )
      .map((f) => extractFolderName(f.folder_path || ""));
    return ALL_PREDEFINED_FOLDER_NAMES.filter((name) => !existingNames.includes(name));
  },

  buildFolderTree(folders: (DocumentFolder | TreeFolder)[]): TreeFolder[] {
    const folderMap = new Map<string, TreeFolder>();
    const rootFolders: TreeFolder[] = [];

    // First pass: create all folder objects with proper type conversion
    folders.forEach((folder) => {
      const anyFolder: any = folder;
      const parentId = anyFolder.parentId !== undefined ? anyFolder.parentId : anyFolder.parent_id;
      const isCustom = anyFolder.isCustom !== undefined ? anyFolder.isCustom : anyFolder.is_custom;

      // Check if this is already a TreeFolder or needs conversion from DocumentFolder
      const name =
        anyFolder.name !== undefined && anyFolder.name !== null
          ? anyFolder.name
          : extractFolderName(anyFolder.folder_path || "");
      const category =
        anyFolder.category !== undefined && anyFolder.category !== null
          ? anyFolder.category
          : extractCategory(anyFolder.folder_config);

      folderMap.set(String(folder.id), {
        id: String(folder.id),
        name,
        parentId: parentId ?? null,
        parent_document_id: anyFolder.parent_document_id
          ? String(anyFolder.parent_document_id)
          : undefined,
        category,
        isCustom: isCustom ?? false,
        building_id: folder.building_id,
        project_id: anyFolder.project_id,
        children: [],
        folder_path: anyFolder.folder_path,
        folder_depth: anyFolder.folder_depth,
        is_project_scoped: anyFolder.is_project_scoped,
        folder_config: anyFolder.folder_config,
      });
    });

    // Second pass: build the tree structure
    folders.forEach((folder) => {
      const anyFolder: any = folder;
      const parentId = anyFolder.parentId !== undefined ? anyFolder.parentId : anyFolder.parent_id;

      const treeFolder = folderMap.get(String(folder.id));
      if (!treeFolder) return;

      if (parentId) {
        const parent = folderMap.get(String(parentId));
        if (parent) {
          parent.children.push(treeFolder);
        } else {
          // Parent doesn't exist, treat as root
          rootFolders.push(treeFolder);
        }
      } else {
        rootFolders.push(treeFolder);
      }
    });

    return sortTreeFolders(rootFolders);
  },

  async createCustomFolder(
    buildingId: string,
    folderName: string,
    category: "documents" | "drawings",
    userId: string,
    orgId: string,
    parentId?: string,
    projectId?: string
  ): Promise<DocumentFolder> {
    // Project ID is now required for custom folders
    if (!projectId) {
      throw new Error("Project ID is required for custom folders");
    }

    // Validate folder name
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      throw new Error("Folder name cannot be empty");
    }

    // WHA-3270: reserve the "Issue Attachments" name — the system folder
    // is auto-created by IssueDocumentUploadButton and must not be
    // duplicated by a user-created folder.
    if (isSystemFolderName(trimmedName)) {
      throw new Error('"Issue Attachments" is a system folder and cannot be created manually');
    }

    // Check for circular reference
    if (parentId && parentId === buildingId) {
      throw new Error("A folder cannot be its own parent");
    }

    // Calculate folder path properly
    let folderPath = `/${trimmedName}`;
    if (parentId) {
      // Get parent folder to construct the path
      const { data: parentFolder, error: parentError } = await supabase
        .from("document_folders")
        .select("folder_path")
        .eq("id", parentId)
        .eq("is_deleted", false)
        .single();

      if (parentError) throw parentError;
      folderPath = `${parentFolder.folder_path}/${trimmedName}`;
    }

    const folderData: any = {
      building_id: parseInt(buildingId, 10),
      project_id: parseInt(projectId, 10),
      name: trimmedName,
      category: category,
      folder_path: folderPath,
      created_by: parseInt(userId),
      org_id: orgId,
      is_custom: true,
      is_project_scoped: true,
      parent_id: parentId ? parseInt(parentId, 10) : null,
      folder_config: { category },
    };

    const { data, error } = await supabase
      .from("document_folders")
      .insert(folderData)
      .select()
      .single();

    if (error) {
      if (error.code === "23505" || error.message.includes("duplicate")) {
        throw new Error("A folder with this name already exists in this location");
      }
      if (error.message.includes("name_validity_check")) {
        throw new Error(
          "Invalid folder name. Please use only letters, numbers, spaces, and basic punctuation."
        );
      }
      throw error;
    }
    return data;
  },

  async updateFolder(
    folderId: string,
    updates: Partial<{ name: string; parent_id: string | null }>,
    userId: string
  ): Promise<DocumentFolder> {
    // Get current folder to determine the update strategy
    const { data: currentFolder, error: fetchError } = await supabase
      .from("document_folders")
      .select("folder_path, parent_id")
      .eq("id", folderId)
      .eq("is_deleted", false)
      .single();

    if (fetchError) throw fetchError;

    const updateData: any = {
      modified_by: parseInt(userId),
      modified_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) {
      // Update name and folder_path based on new name and current parent
      updateData.name = updates.name;
      let parentPathStr = "/";
      if (currentFolder.parent_id) {
        const { data: parent, error: parentErr } = await supabase
          .from("document_folders")
          .select("folder_path")
          .eq("id", currentFolder.parent_id)
          .eq("is_deleted", false)
          .maybeSingle();
        if (parentErr) throw parentErr;
        parentPathStr = parent?.folder_path || "/";
      }
      const base = parentPathStr === "/" ? "" : parentPathStr;
      updateData.folder_path = `${base}/${updates.name}`;
    }

    if (updates.parent_id !== undefined) {
      updateData.parent_id = updates.parent_id ? parseInt(updates.parent_id, 10) : null;
    }

    const { data, error } = await supabase
      .from("document_folders")
      .update(updateData)
      .eq("id", folderId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Move folder to a new parent (supports drag & drop)
  async moveFolder(
    folderId: string,
    newParentId: string | null,
    userId: string
  ): Promise<DocumentFolder> {
    // Validate move operation
    if (newParentId && newParentId === folderId) {
      throw new Error("A folder cannot be moved to itself");
    }

    // Refuse a move into the folder's own subtree: the tree builder drops a
    // cyclic branch entirely, so every row under the moved folder disappears.
    //
    // Walked over `parent_id`, never `folder_path`. The stored path is built
    // from folder *names* (see createCustomFolder), so no pattern over it can
    // identify a folder by id — the check this replaces asked for a
    // `folder_path` literally equal to `%/<id>/%`, which matched nothing and
    // let every move through.
    //
    // The database refuses the same move (franklin-data-pipeline
    // `update_folder_metadata`, "cannot be moved under its own descendant"),
    // and that is the boundary; this is the message the user can read.
    if (newParentId) {
      const destinationChain = await folderService.getFolderPath(newParentId);
      if (destinationChain.some((ancestor) => String(ancestor.id) === String(folderId))) {
        throw new Error("Cannot move folder to its own descendant");
      }
    }

    const { data, error } = await supabase
      .from("document_folders")
      .update({
        parent_id: newParentId ? parseInt(newParentId) : null,
        modified_by: parseInt(userId),
        modified_at: new Date().toISOString(),
      })
      .eq("id", folderId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Document-folder operations
  async moveDocumentToFolder(
    documentId: string,
    folderId: string | null,
    userId: string
  ): Promise<void> {
    const { error } = await supabase
      .from("documents")
      .update({
        folder_id: folderId ? parseInt(folderId) : null,
        modified_by: parseInt(userId),
        modified_at: new Date().toISOString(),
      })
      .eq("id", documentId);

    if (error) throw documentWriteError(error);
  },

  // Get documents in a specific folder
  async getDocumentsInFolder(folderId: string | null, buildingId: string): Promise<any[]> {
    let query = supabase
      .from("documents")
      .select("*, user_profiles(first_name, last_name)")
      .eq("building_id", parseInt(buildingId, 10))
      .eq("is_deleted", false);

    if (folderId) {
      query = query.eq("folder_id", parseInt(folderId));
    } else {
      query = query.is("folder_id", null);
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get folder children count (for UI display)
  async getFolderStats(folderId: string): Promise<{ documentCount: number; folderCount: number }> {
    const [documentsResult, foldersResult] = await Promise.all([
      supabase
        .from("documents")
        .select("id", { count: "exact" })
        .eq("folder_id", parseInt(folderId))
        .eq("is_deleted", false),
      supabase
        .from("document_folders")
        .select("id", { count: "exact" })
        .eq("parent_id", parseInt(folderId))
        .eq("is_deleted", false),
    ]);

    if (documentsResult.error) throw documentsResult.error;
    if (foldersResult.error) throw foldersResult.error;

    return {
      documentCount: documentsResult.count || 0,
      folderCount: foldersResult.count || 0,
    };
  },

  // Enhanced folder tree building with new fields
  buildEnhancedFolderTree(folders: DocumentFolder[]): TreeFolder[] {
    const folderMap = new Map<string, TreeFolder>();
    const rootFolders: TreeFolder[] = [];

    // First pass: create all folder objects
    folders.forEach((folder) => {
      folderMap.set(String(folder.id), {
        id: String(folder.id),
        name: folder.name || extractFolderName(folder.folder_path || ""),
        parentId: folder.parent_id ? String(folder.parent_id) : null,
        category: extractCategory(folder.folder_config),
        isCustom: folder.is_custom,
        building_id: folder.building_id,
        project_id: folder.project_id,
        children: [],
        folder_path: folder.folder_path,
        folder_depth: folder.folder_depth,
        is_project_scoped: folder.is_project_scoped,
        folder_config: folder.folder_config,
      });
    });

    // Second pass: build tree structure
    folders.forEach((folder) => {
      const treeFolder = folderMap.get(String(folder.id));
      if (!treeFolder) return;

      if (folder.parent_id) {
        const parent = folderMap.get(String(folder.parent_id));
        if (parent) {
          parent.children.push(treeFolder);
        } else {
          rootFolders.push(treeFolder);
        }
      } else {
        rootFolders.push(treeFolder);
      }
    });

    return sortTreeFolders(rootFolders);
  },
};
